// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Questionnaire } from '@intakeform/fhir';
import { apiFetch, type ApiError } from './client.js';
import type {
  QuestionnaireListResponse,
  QuestionnaireResource,
  QuestionnaireSummary,
  TemplateListResponse,
} from './types.js';

export const questionnairesQueryKey = ['questionnaires'] as const;
export const questionnaireQueryKey = (id: string) => ['questionnaires', id] as const;

export function useQuestionnaires() {
  return useQuery<QuestionnaireSummary[]>({
    queryKey: questionnairesQueryKey,
    queryFn: async () => {
      const res = await apiFetch<QuestionnaireListResponse>('/api/questionnaires');
      return res.items;
    },
  });
}

export function useQuestionnaire(id: string | undefined) {
  return useQuery<QuestionnaireResource>({
    queryKey: id !== undefined ? questionnaireQueryKey(id) : ['questionnaires', 'noop'],
    enabled: id !== undefined,
    queryFn: () => apiFetch<QuestionnaireResource>(`/api/questionnaires/${id}`),
  });
}

export interface SaveQuestionnaireInput {
  id?: string | undefined;
  resource: Questionnaire;
  /** Force-fork the questionnaire if the API rejects in-place edit with 409. */
  bumpVersion?: boolean | undefined;
  /** Routable form classification. undefined = leave unchanged; null = clear. */
  formType?: string | null | undefined;
}

export interface LintFinding {
  code: 'LINT_DUPLICATE_LINK_ID' | 'LINT_DUPLICATE_LABEL';
  severity: 'error' | 'warning';
  linkId: string;
  conflictingLinkIds?: string[];
  message: string;
}

export interface SaveQuestionnaireResponse {
  id: string;
  version: number;
  forked?: boolean;
  warnings?: LintFinding[];
}

export function useSaveQuestionnaire() {
  const qc = useQueryClient();
  return useMutation<SaveQuestionnaireResponse, ApiError, SaveQuestionnaireInput>({
    mutationFn: ({ id, resource, bumpVersion, formType }) => {
      const query = bumpVersion === true ? '?bumpVersion=true' : '';
      return id !== undefined
        ? apiFetch(`/api/questionnaires/${id}${query}`, {
            method: 'PUT',
            body: { resource, formType },
          })
        : apiFetch('/api/questionnaires', { method: 'POST', body: { resource, formType } });
    },
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({ queryKey: questionnairesQueryKey });
      if (vars.id !== undefined) {
        await qc.invalidateQueries({ queryKey: questionnaireQueryKey(vars.id) });
      }
    },
  });
}

export function useDeleteQuestionnaire() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (id) => apiFetch(`/api/questionnaires/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: questionnairesQueryKey });
    },
  });
}

export const templatesQueryKey = ['templates'] as const;

export function useTemplates() {
  return useQuery<QuestionnaireResource[]>({
    queryKey: templatesQueryKey,
    queryFn: async () => {
      const res = await apiFetch<TemplateListResponse>('/api/templates');
      return res.items;
    },
  });
}

export function useImportTemplate() {
  const qc = useQueryClient();
  return useMutation<{ id: string; version: number }, ApiError, string>({
    mutationFn: (id) => apiFetch(`/api/templates/${id}/import`, { method: 'POST' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: questionnairesQueryKey });
    },
  });
}

export interface SaveAsTemplateResponse {
  id: string;
  version: number;
  /** Set when an existing org template for the same source form was
   *  silently replaced (soft-deleted). UI toasts "Template updated"
   *  instead of "Template saved" when this is non-null. */
  replacedTemplateId: string | null;
}

/**
 * Server-side promotion of an org-owned form into a reusable
 * org-scoped template. Server enforces:
 *   - ≤5 items, all boolean-typed (template_constraints_failed → 400)
 *   - same-source-form replaces silently (returns replacedTemplateId)
 *   - same content-hash from a *different* source (template_content_duplicate
 *     → 409, details.existingTemplateId)
 */
export function useSaveAsTemplate() {
  const qc = useQueryClient();
  return useMutation<SaveAsTemplateResponse, ApiError, string>({
    mutationFn: (formId) =>
      apiFetch(`/api/questionnaires/${formId}/save-as-template`, { method: 'POST' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: templatesQueryKey });
    },
  });
}

/**
 * Soft-delete an org-scoped template. Reuses the existing
 * DELETE /api/questionnaires/:id endpoint — templates are stored
 * as questionnaire rows with the template-scope=organization
 * extension, so the org-scoped delete naturally applies.
 */
export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (id) => apiFetch(`/api/questionnaires/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: templatesQueryKey });
    },
  });
}
